import { createClient } from '@supabase/supabase-js';
import { 
  SUBSCRIPTION_PLAN,
  SUBSCRIPTION_INTERVAL,
  ProductPrices,
  PriceInfo,
  SubscriptionStatus
} from '../constants/subscription';

/**
 * 获取当前用户的Supabase客户端
 */
const getSupabaseClient = () => {
  return createClient(
    import.meta.env.VITE_SUPABASE_URL,
    import.meta.env.VITE_SUPABASE_ANON_KEY
  );
};

/**
 * 调用Stripe相关的Edge Function
 * @param path 函数路径
 * @param data 请求数据
 * @returns 响应数据
 */
const callStripeFunction = async (path: string, data: any = {}) => {
  const supabase = getSupabaseClient();
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session) {
    throw new Error('User not authenticated');
  }
  
  const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      ...data,
      path,
    }),
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    console.error('Stripe function error:', errorText);
    throw new Error(`Failed to call Stripe function: ${response.status} ${response.statusText}`);
  }
  
  return response.json();
};

/**
 * 从API获取订阅价格信息
 * 
 * 价格信息完全从后端获取，前端不应硬编码任何价格或价格ID
 * 这确保了价格变动时无需更新前端代码，只需在Stripe后台更新即可
 * 
 * @returns 包含所有产品和价格信息的对象，符合ProductPrices接口
 */
export const fetchPrices = async (): Promise<ProductPrices> => {
  try {
    // 调用后端API获取最新价格列表
    const response = await callStripeFunction('get-prices');
    
    // 验证响应格式并转换为前端所需的格式
    if (!response || !response.prices) {
      throw new Error('Invalid price data received from API');
    }
    
    // 返回符合ProductPrices接口的数据
    return response.prices as ProductPrices;
  } catch (error) {
    console.error('Error fetching subscription prices:', error);
    throw new Error('Failed to fetch subscription prices from API');
  }
};

/**
 * 创建Checkout会话
 * @param priceId Stripe价格ID
 * @returns Checkout会话信息
 */
export const createCheckoutSession = async (priceId: string) => {
  try {
    const origin = window.location.origin;
    const response = await callStripeFunction('create-checkout-session', {
      priceId,
      successUrl: `${origin}/subscription/success`,
      cancelUrl: `${origin}/subscription/cancel`,
    });
    
    return response;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw new Error('Failed to create checkout session');
  }
};

/**
 * 获取当前用户的订阅状态
 * @returns 订阅信息
 */
export const getCurrentSubscription = async () => {
  try {
    const response = await callStripeFunction('get-subscription');
    
    // 将snake_case转换为camelCase
    if (response.subscription) {
      return {
        ...response.subscription,
        // 确保关键属性名格式正确
        cancelAtPeriodEnd: response.subscription.cancel_at_period_end,
        currentPeriodEnd: response.subscription.current_period_end,
        stripeCustomerId: response.subscription.stripe_customer_id,
        stripeSubscriptionId: response.subscription.stripe_subscription_id
      };
    }
    
    return response.subscription;
  } catch (error) {
    console.error('Error fetching subscription:', error);
    throw new Error('Failed to fetch subscription');
  }
};

/**
 * 取消用户订阅
 * @param subscriptionId 订阅ID
 * @returns 操作结果
 */
export const cancelSubscription = async (subscriptionId: string) => {
  try {
    const response = await callStripeFunction('cancel-subscription', {
      subscriptionId,
    });
    
    return response;
  } catch (error) {
    console.error('Error canceling subscription:', error);
    throw new Error('Failed to cancel subscription');
  }
};

/**
 * 恢复已取消的订阅
 * @param subscriptionId 订阅ID
 * @returns 操作结果
 */
export const resumeSubscription = async (subscriptionId: string) => {
  try {
    const response = await callStripeFunction('resume-subscription', {
      subscriptionId,
    });
    
    return response;
  } catch (error) {
    console.error('Error resuming subscription:', error);
    throw new Error('Failed to resume subscription');
  }
};

/**
 * 检查用户是否有权访问会员功能
 * @returns 是否为会员
 */
export const checkIsPremium = async (): Promise<boolean> => {
  try {
    // 调用新的会员验证接口
    const response = await fetchApi<{ isActive: boolean }>('/membership/me/status');
    return response.isActive;
  } catch (error) {
    console.error('Error checking membership status:', error);
    return false; // 出错时默认返回非会员状态
  }
};

/**
 * 获取当前活跃订阅计划
 * @returns 当前活跃的订阅计划信息，如果不是会员则返回null
 */
export const getCurrentPlan = async () => {
  try {
    const subscription = await getCurrentSubscription();
    
    // 如果没有订阅记录，则返回null
    if (!subscription || ![SubscriptionStatus.ACTIVE, SubscriptionStatus.TRIALING].includes(subscription.status as any)) {
      return null;
    }
    
    // 获取订阅计划信息
    // 实际应从订阅记录中获取计划详情
    // 此处简化为返回Plus计划
    return {
      name: SUBSCRIPTION_PLAN.PLUS,
      interval: subscription.interval || SUBSCRIPTION_INTERVAL.MONTH,
      status: subscription.status,
      currentPeriodEnd: subscription.currentPeriodEnd,
      cancelAtPeriodEnd: subscription.cancelAtPeriodEnd,
    };
  } catch (error) {
    console.error('Error getting current plan:', error);
    return null;
  }
}; 